import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Redirect, useHistory, useLocation, useParams } from "react-router-dom";
import { CircularProgress, Typography } from "@mui/material";

import Breadcrumbs from "../../../../../../components/BreadCrumbs";
import Button from "../../../../../../components/Button";
import messages from "../../../../../../assets/locale/messages";
import { ROUTE_PATHS } from "../../../../../../utils/RoutesPaths";
import { isSuperAdmin } from "../../../../../../utils/Permissions";
import {
  getCompanyDetailsRequest,
  clearCompanyDetails,
} from "../../../../../../store/RegistrationReq/actions";
import {
  clearCompanyEditRequestDetail,
  getCompanyEditRequestShowRequest,
  patchCompanyEditRequestRequest,
} from "../../../../../../store/CompaniesList/actions";
import CompanyDetailsHeader from "../../components/ComapnyDetailsHeader";
import "../../../../../../components/Company/CompanyViewDetails/CompanyViewDetails.scss";
import CompanyEditRequestBanner from "../../../../../../components/Company/CompanyEditRequestBanner";
import ReviewProfileDiffPanel from "./components/ReviewProfileDiffPanel";

import "./_ReviewProfileUpdateRequestPage.scss";

const ReviewProfileUpdateRequestPage = () => {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const lang = useSelector((s) => s.locale.lang);
  const {
    companyDetails,
    companyDetailsLoading,
    companyDetailsError,
    companyDetailsRequestId,
  } = useSelector((s) => s.companiesRegistration);
  const {
    companyEditRequestDetail: editDetail,
    companyEditRequestDetailLoading: editLoading,
    companyEditRequestDetailError: editError,
    companyEditRequestPatchSubmitting: patchSubmitting,
  } = useSelector((s) => s.companiesList);
  const {
    registrationReq,
    companiesList: cl,
    profileEditRequests: per,
  } = messages[lang];
  const fromProfileEditRequests = Boolean(
    location.state?.fromProfileEditRequests,
  );

  useEffect(() => {
    if (id) dispatch(getCompanyDetailsRequest({ id, force: true }));
    return () => {
      dispatch(clearCompanyDetails());
      dispatch(clearCompanyEditRequestDetail());
    };
  }, [dispatch, id, lang]);

  const editRequestId = companyDetails?.company_edit_request?.id ?? null;
  useEffect(() => {
    if (editRequestId != null) {
      dispatch(getCompanyEditRequestShowRequest({ id: editRequestId }));
    }
  }, [dispatch, editRequestId]);

  const editRequestIdForPatch =
    editDetail?.id ?? editDetail?.requestApiId ?? null;
    
  const navigateAfterDecision = () => {
    history.push(
      fromProfileEditRequests
        ? ROUTE_PATHS.profileEditRequests
        : ROUTE_PATHS.companiesListCompanyDetails.replace(":id", String(id)),
    );
  };

  const handleApprove = () => {
    if (editRequestIdForPatch == null) return;
    dispatch(
      patchCompanyEditRequestRequest({
        editRequestId: String(editRequestIdForPatch),
        companyRouteId: id,
        status: "approved",
        onSuccess: navigateAfterDecision,
      }),
    );
  };

  const handleRejectSubmit = (reason) => {
    if (editRequestIdForPatch == null) return;

    dispatch(
      patchCompanyEditRequestRequest({
        editRequestId: String(editRequestIdForPatch),
        companyRouteId: id,
        status: "rejected",
        rejectionReason: reason ?? "",
        onSuccess: navigateAfterDecision,
      }),
    );
  };

  const companyDetailsPath = id
    ? ROUTE_PATHS.companiesListCompanyDetails.replace(":id", String(id))
    : "";
  const companyBreadcrumbName =
    companyDetails?.name_en || companyDetails?.name || null;

  const breadcrumbsBar = (
    <div className="mt-3">
      <Breadcrumbs
        list={[
          fromProfileEditRequests
            ? { path: ROUTE_PATHS.profileEditRequests, label: per.listBreadcrumb }
            : { path: ROUTE_PATHS.companiesList, label: cl.listBreadcrumb },
          ...(companyBreadcrumbName
            ? [{ path: companyDetailsPath, label: companyBreadcrumbName }]
            : []),
          { path: "", label: cl.reviewProfileUpdateRequest },
        ]}
        BreadCrumbHasBackIcon={false}
      />
    </div>
  );

  if (!isSuperAdmin()) return <Redirect to={ROUTE_PATHS.events} />;

  if (companyDetailsError) {
    return (
      <div className="registration-req-page">
        {breadcrumbsBar}
        <div className="py-4 text-center">
          <Typography color="error" className="mb-3">
            {companyDetailsError || registrationReq.companyDetailsLoadError}
          </Typography>
          <Button
            label={registrationReq.retry}
            onClick={() => dispatch(getCompanyDetailsRequest({ id, force: true }))}
          />
        </div>
      </div>
    );
  }

  const mismatched =
    companyDetails &&
    companyDetailsRequestId &&
    String(companyDetailsRequestId) !== String(id);

  if ((companyDetailsLoading && !companyDetails) || mismatched) {
    return <div className="registration-req-page">{breadcrumbsBar}</div>;
  }

  if (!companyDetails) return null;

  const bannerDate =
    editDetail?.updated_at || editDetail?.created_at || companyDetails.updated_at;

  if (editRequestId == null) {
    return (
      <Redirect
        to={ROUTE_PATHS.companiesListCompanyDetails.replace(":id", String(id))}
      />
    );
  }

  return (
    <div className="registration-req-page review-profile-update-request-page">
        <CompanyDetailsHeader
          companyDetails={companyDetails}
          id={id}
          extraBreadcrumbs={[{ path: "", label: cl.reviewProfileUpdateRequest }]}
          leadingBreadcrumbs={
            fromProfileEditRequests
              ? [{ path: ROUTE_PATHS.profileEditRequests, label: per.listBreadcrumb }]
              : undefined
          }
        />
          <CompanyEditRequestBanner
            isReviewRequestOpen
            submittedAt={bannerDate}
          />
        <div className="company-view-details company-details-page--companies-list">
          {editError ? (
            <div className="py-3 text-center">
              <Typography color="error" className="mb-2">
                {editError || cl.editRequestLoadError}
              </Typography>
              <Button
                label={registrationReq.retry}
                onClick={() =>
                  dispatch(getCompanyEditRequestShowRequest({ id: editRequestId }))
                }
              />
            </div>
          ) : editLoading && !editDetail ? (
            <div className="d-flex justify-content-center py-5">
              <CircularProgress size={40} />
            </div>
          ) : editDetail ? (
            <ReviewProfileDiffPanel
              editDetail={editDetail}
              lang={lang}
              registrationReq={registrationReq}
              cl={cl}
              patchSubmitting={patchSubmitting}
              onApprove={handleApprove}
              onRejectSubmit={handleRejectSubmit}
            />
          ) : null}
        </div>
    </div>
  );
};

export default ReviewProfileUpdateRequestPage;
